import { Plugin } from "./types";
import { AnalyticsModuleBootstrapError, logger } from "../debug";

// TODO: Add timeout too
export function loadedSimulation<T>(
  fn: (...args: T[]) => boolean,
  ...args: T[]
) {
  return new Promise<void>((res, rej) => {
    const poll = setInterval(() => {
      try {
        if (fn(...args) === true) {
          clearInterval(poll);
          res();
        }
      } catch (e) {
        clearInterval(poll);
        rej(e);
      }
    }, 1);
  });
}

const INIT_FNS = [
  "bootstrap",
  "registerPlugins",
  "initializeStart",
  "initialize",
  "initializeEnd",
  "loaded",
  "ready",
] as const;

type InitFnMap = { [key in (typeof INIT_FNS)[number]]?: (...args) => unknown };
const wrappers: {
  [T in keyof InitFnMap]: {
    message?: Record<PropertyKey, unknown>;
    function: InitFnMap[T];
  };
} = {
  loaded: {
    function: loadedSimulation,
  },
};

class AbortFunctionError extends Error {
  constructor(msg?: string) {
    super(msg);
    this.name = "AbortFunctionError";
  }
}

export const AnalyticsLite = (function () {
  type EmulatedPluginFN = {
    fn: (...args) => void;
    un: () => void;
  };
  type EmulatedPluginMap = Map<string, EmulatedPluginFN>;
  const emulatedPlugins = new Map<string, EmulatedPluginMap>();
  const pluginState = {};

  function registerMethods(plugin: Plugin) {
    emulatedPlugins.set(plugin.name, new Map());
    // For Later
  }

  class AbortError extends Error {
    constructor(message?: string) {
      super(message);
    }
  }

  type PluginFnArgs = [{ abort?: (message?: unknown) => void }];
  function getArgsForFn(fnName: (typeof INIT_FNS)[number]) {
    // TODO: expand later for all events
    const args: PluginFnArgs = [{}];
    if (fnName.includes("Start")) {
      args[0].abort = (message?: string) => {
        throw new AbortError(message);
      };
    }
    return args;
  }

  function* pluginInit(plugin: Plugin) {
    const initFnMap = INIT_FNS.reduce((fnMap, fn) => {
      if (plugin[fn] !== undefined) {
        fnMap[fn] = plugin[fn];
      }
      return fnMap;
    }, {} as InitFnMap);

    function execStep(
      name: (typeof INIT_FNS)[number],
      wrapper?: Function,
      message?: Record<PropertyKey, unknown>
    ) {
      const args = getArgsForFn(name);
      return initFnMap[name] !== undefined
        ? wrapper !== undefined
          ? // TODO: Provide proper mocks in the future
            wrapper(initFnMap[name], args)
          : initFnMap[name](...args)
        : Promise.resolve(
            message ?? { message: `[${plugin.name}] No ${name} function` }
          );
    }

    // For reference: https://getanalytics.io/lifecycle/
    // Since object key order is not guarenteed we'll refer to the order of the INIT_FNS
    for (const fn of INIT_FNS) {
      yield execStep(fn, wrappers[fn]?.function, wrappers[fn]?.message);
      if (fn === "initializeEnd") {
        registerMethods(plugin);
      }
    }
  }

  interface MissedEventMeta {
    ts: number;
    rid: string;
    hasCallback: boolean;
  }

  type MissedEventProperties = Record<string | number, any>;

  type MissedEvent = {
    meta: MissedEventMeta;
    event: string;
    properties: MissedEventProperties;
  };

  async function processMissedEvents(plugin: Plugin, events: MissedEvent[]) {
    for (const event of events) {
      let trackStartParams;
      if (plugin.trackStart) {
        try {
          trackStartParams = {
            payload: event,
            instance: window.analytics.analytics,
            config: plugin.config,
            abort: () => {
              throw new AbortFunctionError();
            },
          };
          const trackStart = plugin.trackStart(
            trackStartParams
          ) as unknown as Promise<unknown>;

          if (trackStart.then) {
            await trackStart;
          }
        } catch (e) {
          if (e instanceof AbortFunctionError) {
            if (plugin.config.debug) {
              logger.error(e);
            }
            return;
          } else {
            throw e;
          }
        }
      }
      let trackParams;
      if (plugin.track) {
        trackParams = {
          payload: trackStartParams?.event ?? event,
          instance: window.analytics.analytics,
          config: plugin.config,
        };
        try {
          const track = plugin.track(
            trackParams
          ) as unknown as Promise<unknown>;

          if (track.then) {
            await track;
          }
        } catch (e) {
          logger.error(e);
          return;
        }
      }

      if (plugin.trackEnd) {
        const trackEndParams = {
          payload: trackParams?.payload ?? event,
          instance: window.analytics.analytics,
          config: plugin.config,
        };

        try {
          const trackEnd = plugin.trackEnd(
            trackEndParams
          ) as unknown as Promise<unknown>;

          if (trackEnd.then) {
            await trackEnd;
          }
        } catch (e) {
          logger.error(e);
          return;
        }
      }
    }
  }

  function enablePlugin(pluginName: string) {
    const state = pluginState[pluginName];
    const pluginFns = emulatedPlugins.get(pluginName);
    if (pluginFns === undefined) {
      return { name: pluginName, message: "Was not simulated!" };
    }
    if (state?.enabled === true) {
      return { name: pluginName, message: "already enabled" };
    }
    try {
      if (pluginFns.has("enablePlugin")) {
        pluginFns.get("enablePlugin").fn();
      }
      pluginState[pluginName] = {
        ...state,
        enabled: true,
      };
    } catch (e) {
      logger.error(
        new AnalyticsModuleBootstrapError(
          `[${pluginName}] Error while calling enablePlugin`,
          {
            cause: e,
          }
        )
      );
    }

    return { name: pluginName, message: "enabled" };
  }

  function disablePlugin(pluginName: string) {
    const state = pluginState[pluginName];
    const pluginFns = emulatedPlugins.get(pluginName);
    if (pluginFns === undefined) {
      return { name: pluginName, message: "Was not simulated!" };
    }
    if (state?.enabled === false) {
      return { name: pluginName, message: "already disabled" };
    }

    if (pluginFns.has("disablePlugin")) {
      try {
        pluginFns.get("disablePlugin").fn();
      } catch (e) {
        logger.error(
          new AnalyticsModuleBootstrapError(
            `[${pluginName}] Error while calling disablePlugin`,
            {
              cause: e,
            }
          )
        );
      }
    }

    pluginState[pluginName] = {
      ...state,
      enabled: false,
    };

    return { name: pluginName, message: "disabled" };
  }

  function removePlugin(pluginName: string) {
    try {
      disablePlugin(pluginName);
      const pluginFns = emulatedPlugins.get(pluginName);
      if (pluginFns === undefined) {
        logger.warn(
          new AnalyticsModuleBootstrapError(
            `Was not able to find plugin [${pluginName}] to remove`
          )
        );
        return { name: pluginName, message: "Was not simulated!" };
      }
      pluginFns.forEach((pluginFn, fnName) => {
        try {
          pluginFn.un();
        } catch (e) {
          logger.error(
            new AnalyticsModuleBootstrapError(
              `[${pluginName}] Error while cleaning up ${fnName}`,
              {
                cause: e,
              }
            )
          );
        }
      });
      emulatedPlugins.delete(pluginName);
    } catch (e) {
      logger.error(
        new AnalyticsModuleBootstrapError(
          `Error while removing plugin [${pluginName}]`,
          {
            cause: e,
          }
        )
      );
    }
  }

  async function addPlugin(plugin: Plugin) {
    if (emulatedPlugins.has(plugin.name)) {
      return { name: plugin.name, message: "already exists" };
    }

    try {
      for await (const fnRes of pluginInit(plugin)) {
        if (plugin.config?.debug) {
          logger.debug(plugin.name, "plugin finished", fnRes);
        }
      }

      const currentPlugin = emulatedPlugins.get(plugin.name);
      Object.keys(plugin)
        .filter((pluginKey) => {
          return (
            !INIT_FNS.includes(pluginKey as (typeof INIT_FNS)[number]) &&
            typeof plugin[pluginKey] === "function"
          );
        })
        .forEach((fnToAdd) => {
          currentPlugin.set(fnToAdd, {
            fn: plugin[fnToAdd],
            un: window.analytics.analytics.on(fnToAdd, (...args) => {
              if (pluginState[plugin.name]?.enabled === false) {
                return;
              }
              if (!fnToAdd.includes(":")) {
                const { payload } = args[0] || {};
                const forAll = payload?.options?.plugins?.all ?? true;
                if (forAll === false) {
                  return;
                }
              }
              return plugin[fnToAdd].apply(this, args);
            }),
          });
        });

      pluginState[plugin.name] = {
        ready: true,
      };

      enablePlugin(plugin.name);
    } catch (e) {
      if (e instanceof AbortError) {
        if (plugin.config?.debug) {
          logger.error(
            new AnalyticsModuleBootstrapError(
              `Abort was fired for [${plugin.name}]`,
              {
                cause: e,
              }
            )
          );
        }
      } else {
        logger.error(
          new AnalyticsModuleBootstrapError(
            `There was an error while initializing [${plugin.name}]`,
            {
              cause: e,
            }
          )
        );
      }
    }
  }

  return {
    enablePlugin,
    disablePlugin,
    addPlugin,
    removePlugin,
    processMissedEvents,
    isPluginEnabled(pluginName: string) {
      return pluginState[pluginName]?.enabled === true;
    },
    emulatedPlugins,
    getState() {
      return pluginState;
    },
  };
})();
